import {Rule} from 'Event/attendee-rules'
import {Groups, Tags} from 'Event/attendee'
import {
  DOES_NOT_INCLUDE,
  TAGS,
  TagsRule,
} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/TagsRule'
import {
  matchesTag,
  matchesTagsRule,
} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/TagsRule/matcher'
import {
  GROUP,
  GroupRule,
  IS_BLANK,
  IS_FILLED,
  IS_NOT,
} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/GroupRule'
import {
  matchesGroupKey,
  matchesGroupRule,
} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/GroupRule/matcher'
import {
  DOES_NOT_CONTAIN,
  FORM_RESPONSE,
  FormResponseRule,
  IS_NOT as FORM_RESPONSE_IS_NOT,
} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/FormResponseRule'
import {
  matchesQuestion,
  matchesFormResponseRule,
} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/FormResponseRule/matcher'
import {
  TIME,
  TimeRule,
} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/TimeRule'
import {matchesTimeRule} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/TimeRule/matcher'
import {NESTED_RULE} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/NestedRule'
import {Answer} from 'Event/SubmissionsProvider'
import {Form} from 'organization/Event/FormsProvider'
import {uuid} from 'lib/uuid'

export function getValuesMatchingRules(params: {
  rules: Rule[]
  groups: Groups
  tags: Tags
  answers: Answer[]
  date: string | null
  forms: Form[]
}): {groups: Groups; tags: Tags; answers: Answer[]; date: string | null} {
  const {rules, forms, groups, tags, answers, date} = params

  return rules.reduce(
    (values, rule) => {
      switch (rule.source) {
        case NESTED_RULE:
          return getValuesMatchingRules({...values, rules: rule.rules, forms})
        case TAGS:
          return {...values, tags: getTagsMatchingRule(values.tags, rule)}
        case GROUP:
          return {...values, groups: getGroupsMatchingRule(values.groups, rule)}
        case FORM_RESPONSE:
          return {
            ...values,
            answers: getAnswersMatchingRule({
              answers: values.answers,
              rule,
              forms,
            }),
          }
        case TIME:
          return {...values, date: getDateMatchingRule(values.date, rule)}
        default:
          return values
      }
    },
    {groups, tags, answers, date},
  )
}

function getTagsMatchingRule(tags: Tags, rule: TagsRule): Tags {
  if (!rule.target || matchesTagsRule(tags, rule)) {
    return tags
  }

  if (rule.type === DOES_NOT_INCLUDE) {
    return tags.filter((tag) => !matchesTag(tag, rule))
  }

  return [...tags, rule.target]
}

function getGroupsMatchingRule(groups: Groups, rule: GroupRule): Groups {
  if (!rule.key || matchesGroupRule(groups, rule)) {
    return groups
  }

  const otherGroups = Object.fromEntries(
    Object.entries(groups).filter(([key]) => !matchesGroupKey(key, rule)),
  )

  if (rule.type === IS_NOT || rule.type === IS_BLANK) {
    return otherGroups
  }

  if (rule.type === IS_FILLED) {
    return {...otherGroups, [rule.key]: rule.target || 'true'}
  }

  return {...otherGroups, [rule.key]: rule.target}
}

function getAnswersMatchingRule(params: {
  answers: Answer[]
  rule: FormResponseRule
  forms: Form[]
}): Answer[] {
  const {answers, rule, forms} = params

  if (matchesFormResponseRule({answers, forms, rule})) {
    return answers
  }

  const otherAnswers = answers.filter(
    (answer) => !matchesQuestion(answer, rule),
  )

  if (rule.type === FORM_RESPONSE_IS_NOT || rule.type === DOES_NOT_CONTAIN) {
    return otherAnswers
  }

  const answer: Answer = {
    id: uuid(),
    form_id: rule.form_id,
    question_id: Number(rule.question_id),
    value: rule.value,
  }

  return [...otherAnswers, answer]
}

function getDateMatchingRule(
  date: string | null,
  rule: TimeRule,
): string | null {
  if (!rule.from) {
    return date
  }

  if (date && matchesTimeRule(rule, date)) {
    return date
  }

  return rule.from
}
